import React from "react"
import { Checkbox } from "../../Shadcn/Checkbox"
import { TooltipProvider, TooltipRoot, TooltipTrigger, TooltipContent } from "../../Shadcn/Tooltip"
import { Typography } from "../../Shadcn/Typography"
import { selAggLayerState, checkboxState, hexLoadingState } from "../../../states"
import { useRecoilValue } from "recoil"
import InfoIcon from "../../../utils/images/information--filled.svg"

export interface LayerCheckboxRowProps {
  layer: any
  isChecked: boolean
  disabled?: boolean
  onCheckedChange: (checked: boolean, name: string) => void
  customTooltip?: string
  /** Secondary text shown beneath the label when checked (e.g. "Showing: Districts") */
  infoText?: string | null
  /** Shows a spinner next to the label */
  loading?: boolean
  className?: string
  /** Node rendered to the left of the label (e.g. a color indicator dot) */
  leftDecoration?: React.ReactNode
}

export const LayerCheckboxRow: React.FC<LayerCheckboxRowProps> = ({
  layer,
  isChecked,
  disabled = false,
  onCheckedChange,
  customTooltip,
  infoText,
  className,
  leftDecoration,
}) => {
  const selAggLayer = useRecoilValue(selAggLayerState)
  const isCheckedRegion = useRecoilValue(checkboxState)
  const hexLoading = useRecoilValue(hexLoadingState)
  const [infoTooltipOpen, setInfoTooltipOpen] = React.useState(false)

  const isAdjacencyLoading = layer.type === "adjacency" && hexLoading.loading && isChecked

  const getToggleKey = () => {
    const knownToggles = [
      "EH", "CTV", "PoI", "MBP", "spotlight", "poimarker",
      "adjacency", "subregionBoundaries", "catchments",
    ]
    if (knownToggles.includes(layer.type)) return layer.type
    if (layer?.markertype?.toLowerCase() === "poi") return layer.type
    return layer.layer_id || layer.type
  }
  const toggleKey = getToggleKey()

  const tooltipText = customTooltip
    ? customTooltip
    : selAggLayer == null
    ? "Turn on Choropleth to enable this toggle."
    : layer?.type === "CTV" && isCheckedRegion?.catchments
    ? "Disabled due to Catchment"
    : `We do not have ${layer.type} at this level`

  const showTooltip =
    disabled &&
    (customTooltip ||
      layer.type === "EH" ||
      layer.type === "CTV" ||
      layer.type === "MBP" ||
      layer.type === "catchments" ||
      layer.type === "PoI")

  return (
    <div className={`flex flex-col gap-0.5 w-full ${className ?? ""}`}>
      <div className="flex items-center justify-between w-full">
        {/* Label side */}
        <div className="flex items-center gap-1 flex-1 min-w-0" title={layer?.title}>
          {leftDecoration}
          {layer?.status=="inprogress" && (
            <span
              className="spinner-border spinner-border-sm shrink-0"
              role="status"
              style={{ borderWidth: "0.1em", color: "#adb5bd", width: "0.75em", height: "0.75em" }}
            />
          )}
          <Typography
            variant="b2"
            className={`truncate font-brand font-medium text-xs leading-[1.6] text-[var(--ds-solid-neutral-12)] ${disabled || layer?.status === "inprogress" ? "opacity-50" : ""}`}
          >
            {layer.title}
            {layer.status === "inprogress" && (
              <span
                className="spinner-border spinner-border-sm ms-1"
                role="status"
                style={{ borderWidth: "0.1em", color: "#adb5bd", width: "0.75em", height: "0.75em", verticalAlign: "middle" }}
              />
            )}
          </Typography>

          {layer.description && (
            <TooltipProvider>
              <TooltipRoot open={infoTooltipOpen} onOpenChange={setInfoTooltipOpen}>
                <TooltipTrigger asChild>
                  <span
                    className="cursor-pointer shrink-0 leading-none flex items-center"
                    onClick={(e) => { e.stopPropagation(); setInfoTooltipOpen((v) => !v); }}
                  >
                    <img src={InfoIcon} alt="info" className="h-3 w-3" />
                  </span>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-48 text-xs text-[var(--general-text-primary)] bg-[var(--general-molecules-bg-bg-white)] shadow-md">
                  <span dangerouslySetInnerHTML={{ __html: layer.description?.replace(/\\n/g, "<br>") }} />
                </TooltipContent>
              </TooltipRoot>
            </TooltipProvider>
          )}
        </div>

        {/* Checkbox side */}
        <div className="flex items-center shrink-0 pl-2">
          {"isselected" in layer ? (
            isAdjacencyLoading ? (
              <span
                className="spinner-border spinner-border-sm"
                role="status"
                style={{ borderWidth: "0.1em", color: "#adb5bd" }}
              />
            ) : (
              <TooltipProvider>
                <TooltipRoot open={showTooltip ? undefined : false}>
                  <TooltipTrigger asChild>
                    <span>
                      <Checkbox
                        id={toggleKey}
                        checked={isChecked}
                        disabled={disabled || layer?.status === "inprogress"}
                        onCheckedChange={(val) => onCheckedChange(!!val, toggleKey)}
                      />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top">{tooltipText}</TooltipContent>
                </TooltipRoot>
              </TooltipProvider>
            )
          ) : null}
        </div>
      </div>

      {/* Secondary info text (e.g. "Showing: Districts") */}
      {infoText && (
        <Typography variant="b3" className="text-[var(--ds-solid-neutral-11)] font-normal pl-0">
          {infoText}
        </Typography>
      )}

      {layer?.status=="inprogress"&& (
        <Typography variant="b3" className="text-muted-foreground pl-0 text-[0.625rem]">
          Loading data...
        </Typography>
      )}
    </div>
  )
}
